import Swiper from 'swiper'
import { Navigation } from 'swiper/modules'
import { NostoHelper } from 'helpers/nosto'
import { getProductRecommendations } from 'utils/requests'
import { imagesLoaded } from 'utils/gw'
import { lowPriority } from 'lib/performance'

// TODO: Cache recommendations for ~24 hours? (localStorage or Cache)
class NostoRecommendations extends HTMLElement {
  constructor() {
    super()
    this.productId = this.getAttribute('nosto-id')
    this.nostoPlacementId = this.getAttribute('nosto-placement-id') || false
    this.sectionName = this.getAttribute('section-name') || 'api-product-card'
    this.productSlides = []
    this.trigger = this.getAttribute('load-on') ?? 'init'

    this.slidesPerView =
      Number.parseFloat(this.getAttribute('slides-per-view')) || 'auto'
    this.slidesPerViewTablet =
      Number.parseFloat(this.getAttribute('slides-per-view-tablet')) || 'auto'
    this.slidesPerViewDesktop =
      Number.parseFloat(this.getAttribute('slides-per-view-desktop')) || 'auto'
    this.flex = Boolean(this.getAttribute('flex'))
    this.spaceBetween = Number.parseInt(this.getAttribute('space-between')) || 0
    this.centeredSlides = Boolean(this.getAttribute('centered-slides'))
    this.loop = Boolean(this.getAttribute('loop'))

    this.init()
  }

  get storageId() {
    this.storedMarkupId ??= `nosto-recommendations-${this.nostoPlacementId}${this.productId ? `-${this.productId}` : ''
      }`
    return this.storedMarkupId
  }

  get storedRecommendations() {
    this.storedMarkup ??= localStorage.getItem(this.storageId)
    return this.storedMarkup
  }

  set storedRecommendations(markup) {
    this.storedMarkup = markup
    localStorage.setItem(this.storageId, markup)
  }

  get hasRecommendations() {
    return this.wrapper.childNodes.length > 0
  }

  init() {
    this.breakpoints = {
      750: {
        slidesPerView: this.flex ? 'auto' : this.slidesPerViewTablet,
        centeredSlides: false,
        loop: false,
      },
      1200: {
        slidesPerView: this.flex ? 'auto' : this.slidesPerViewDesktop,
        centeredSlides: false,
        loop: false,
      },
    }

    this.style.setProperty('--slides-per-view-mobile', this.slidesPerView)
    this.style.setProperty('--slides-per-view-tablet', this.slidesPerViewTablet)
    this.style.setProperty(
      '--slides-per-view-desktop',
      this.slidesPerViewDesktop
    )
    this.style.setProperty('--space-between', `${this.spaceBetween}px`)

    if (this.storedRecommendations) {
      console.log('HAS STORED RECOMMENDATIONS')
      const storedRecommendationKeys = Object.keys(localStorage).filter((key) =>
        key.startsWith('nosto-recommendations')
      )
      for (const key of storedRecommendationKeys) {
        localStorage.removeItem(key)
      }

      // console.debug(
      //   'Loading recommendations from storage for nosto placement ID:',
      //   this.nostoPlacementId
      // )
      // this.wrapper.innerHTML = this.storedRecommendations
    }

    switch (this.trigger) {
      case 'init':
        this.load()
        break
      case 'intersect':
      case 'intersection':
        this.initObserver()
        break
      case 'idle':
        lowPriority(this.init.bind(this))
        break
    }
  }

  loadOnIntersection(entries, observer) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.load()
        observer.disconnect()
      }
    })
  }

  initObserver() {
    const offset = this.getAttribute('intersection-offset') || '200%'
    this.handleIntersection = this.loadOnIntersection.bind(this)
    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: `0px 0px ${offset} 0px`,
    })

    this.observer.observe(this)
  }

  async load() {
    if (this.hasRecommendations) this.initSlider()

    console.debug('Fetching nosto recommendations...')
    if (this.nostoPlacementId) {
      this.waitForNostoToLoad()
    } else {
      try {
        const response = await fetch(this.dataset.url)
        const responseText = await response.text()
        const html = document.createElement('div')
        html.innerHTML = responseText
        const recommendations = html.querySelector('nosto-recommendations')
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML
        }

        this.initSlider()
      } catch (e) {
        console.error('Failed to fetch nosto recommendations', e)
      }
    }
  }

  async getProduct(url) {
    url = url.replace('https://brochuwalker.myshopify.com', 'https://www.brochuwalker.com');
    return await fetch(`${url}?section_id=${this.sectionName}`).then(
      async (response) => {
        const product = await response.text()
        const fresh = document.createElement('div')
        fresh.innerHTML = product

        return `<div class="swiper-slide">${fresh.querySelector('[data-api-content]').innerHTML
          }</div>`
      }
    )
  }

  get wrapper() {
    this.sliderWrapper =
      this.sliderWrapper ??
      this.querySelector(`.js-nosto-recommendations-slider .swiper-wrapper`)

    return this.sliderWrapper
  }

  get placeholder() {
    this.placeholderElement =
      this.placeholderElement ??
      this.querySelector(`.js-nosto-recommendations-slider .placeholder-slide`)

    return this.placeholderElement
  }

  async loopProducts(realSlotIdRendered, products, count = 0, shown = 0) {
    count = count ? count : products.length - 1
    if (shown == 0) {
      this.productSlides = []
      if (!this.hasRecommendations) {
        this.wrapper.style.display = 'none'
        this.wrapper.style.opacity = 0
      }
    }
    if (shown <= count) {
      fetch(`${products[shown].url}?section_id=${this.sectionName}`).then(
        async (response) => {
          const product = await response.text()
          const fresh = document.createElement('div')
          fresh.innerHTML = product

          fresh
            .querySelector('.js-anchor')
            .setAttribute(
              'href',
              `${fresh
                .querySelector('.js-anchor')
                .getAttribute('href')}/?nosto=${realSlotIdRendered}`
            )

          fresh
            .querySelector('.card-information__wrapper > a')
            .setAttribute(
              'href',
              `${fresh
                .querySelector('.card-information__wrapper > a')
                .getAttribute('href')}/?nosto=${realSlotIdRendered}`
            )

          const slide = document.createElement('div')
          slide.classList.add('swiper-slide')
          slide.appendChild(fresh.querySelector('[data-api-content]'))

          this.productSlides.push(slide)

          shown++
          this.loopProducts(realSlotIdRendered, products, count, shown)
        }
      )
    } else {
      await this.updateRecommendations()
    }
  }

  async updateRecommendations() {
    // const hasCachedRecommendations = this.hasRecommendations

    // if (hasCachedRecommendations) {
    //   await imagesLoaded(this.productSlides, { container: true })
    // }

    this.placeholder?.remove()
    this.wrapper.replaceChildren(...this.productSlides)
    const hasOptions = this.sectionName === 'api-product-card'
    NostoHelper.triggerMigration(`#${this.id}`, hasOptions)
    // this.storedRecommendations = this.wrapper.innerHTML
    console.debug(
      'Updating nosto recommendations for nosto placement ID:',
      this.nostoPlacementId
    )

    // if (!hasCachedRecommendations) {
    this.wrapper.style.display = 'inline-flex'
    this.wrapper.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 300,
      fill: 'forwards',
      easing: 'ease-out',
    })
    // }
    this.initSlider()
  }

  async getRecommendedProducts() {
    if (this.nostoPlacementId && window.nostojs) {
      window.nostojs((api) => {
        NostoHelper.viewEvent(api.defaultSession())
          .setPlacements(
            this.nostoPlacementId
              ? [this.nostoPlacementId]
              : api.placements.getPlacements()
          )
          .load()
          .then(async (response) => {
            const { recommendations } = response.campaigns

            let products = {}
            let realSlotIdRendered = ''

            if (this.nostoPlacementId) {
              products = recommendations[this.nostoPlacementId].products
              realSlotIdRendered =
                recommendations[this.nostoPlacementId].result_id
            } else {
              for (const property in recommendations) {
                if (
                  recommendations[property]['title'].includes(
                    'You Might Also Like'
                  ) ||
                  recommendations[property]['title'].includes(
                    'Still Interested In These?'
                  )
                ) {
                  products = recommendations[property].products
                  realSlotIdRendered = recommendations[property].result_id
                  break
                }
              }
            }

            if (products.length > 0) {
              this.style.display = 'block'
              this.loopProducts(realSlotIdRendered, products)
            } else {
              this.style.display = 'none'
            }
          })
      })

      return
    }

    const productRecommendations = await getProductRecommendations({
      product_id: this.productId,
      limit: 1,
      intent: 'related',
    })

    if (productRecommendations.products.length === 0) return

    this.renderProductRecommendation(productRecommendations.products[0])
  }

  waitForNostoToLoad() {
    let limitInterval = 0
    const checkInterval = setInterval(() => {
      if (typeof window.nostojs === 'function' || limitInterval >= 10) {
        this.getRecommendedProducts()
        clearInterval(checkInterval)
      }

      limitInterval++
    }, 500)
  }

  initSlider() {
    const { breakpoints } = this
    new Swiper('.js-nosto-recommendations-slider', {
      modules: [Navigation],
      spaceBetween: this.spaceBetween,
      slidesPerView: this.slidesPerView,
      centeredSlides: this.centeredSlides,
      loop: this.loop,
      breakpoints,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
    })
  }
}

if (!customElements.get('nosto-recommendations')) {
  customElements.define('nosto-recommendations', NostoRecommendations)
}